import React, {
  HTMLAttributes,
  RefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import { useComboBox } from '@react-aria/combobox';
import { FocusScope } from '@react-aria/focus';
import { PressResponder, useFocus, useHover } from '@react-aria/interactions';
import { DismissButton, useOverlayTrigger } from '@react-aria/overlays';
import { useSelect } from '@react-aria/select';
import { mergeProps } from '@react-aria/utils';
import { useProviderProps } from '@react-spectrum/provider';
import { useFocusableRef } from '@react-spectrum/utils';
import { ComboBoxState, useComboBoxState } from '@react-stately/combobox';
import { SpectrumComboBoxProps } from '@react-types/combobox';
import { FocusableRef, LoadingState } from '@react-types/shared';
import cn from 'classnames';

import {
  ComboBoxButton,
  FocusRing,
  getMobileComboboxWrapperStyles,
  Label,
  ListBoxBase,
  Loader,
  Tray,
  useListBoxLayout
} from '@nw/omega-design-system';

import '@nw/omega-design-system/PopOver/index.css';
import '@nw/omega-design-system/Tray/index.css';

import { CrossIcon } from '@nw/shared/ui';
import { getDataMaskAttribute, LOADING } from '@nw/shared/utils';

import { TextFieldBase } from '../TextFieldBase';
import { TextFieldHint } from '../TextFieldHint';

import { ComboBoxPropsTypes } from './Combobox';
import { Container } from './styledComponents';

import './styles.scss';

export interface MobileComboBoxProps {
  error: string | undefined;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  buttonStyles?: string;
  popOverStyles?: string;
  isDisabled?: boolean;
  autoFocus?: boolean;
  parentRef?: RefObject<HTMLDivElement>;
  isSearchable?: boolean;
  label?: string;
  loadingState?: LoadingState;
  onLoadMore?: () => any;
  comboboxButtonClassname?: string;
  cancelButtonClassname?: string;
  getDisplayValueFromSelectedState?: (T: any) => string;
  mobilePlaceholder?: string;
  handleKeyDown?: (e: React.KeyboardEvent) => void;
}

interface CombobboxTrayProps extends MobileComboBoxProps {
  state: ComboBoxState<any>;
  unwrappedTriggerRef: RefObject<HTMLElement>;
  overlayProps: HTMLAttributes<HTMLElement>;
  onClose: () => void;
  cancelButtonClassname?: string;
  enableDataMask?: boolean;
}

export const MobileComboBox = function MobileComboBox(
  props: MobileComboBoxProps & SpectrumComboBoxProps<any> & ComboBoxPropsTypes
) {
  props = useProviderProps(props);

  const {
    helperText,
    error,
    isDisabled,
    isSearchable,
    trayChildrenWrapperClassName,
    trayContainerClassName,
    label,
    comboboxButtonClassname,
    getDisplayValueFromSelectedState = (state) => state.value,
    list,
    selectedKey,
    getInputTextFromValue,
    onSelectionChange,
    autoFocus,
    onInputChange,
    mobilePlaceholder,
    hintClassName,
    enableDataMask
  } = props;

  const isAsync = list.loadingState != null;

  const onSelectionChangeFunc = (value) => {
    list.setSelectedKeys(new Set([value]));
    list.setFilterText(getInputTextFromValue(value, list.items));
    onSelectionChange(value);
  };

  const onChange = (value: string) => {
    list.setFilterText(value);
    onInputChange(value);
  };

  const state = useComboBoxState({
    ...props,
    allowsEmptyCollection: isAsync,
    items: list.items,
    autoFocus,
    selectedKey: selectedKey,
    onSelectionChange: onSelectionChangeFunc,
    onInputChange: onChange,
    inputValue: list.filterText,
    shouldCloseOnBlur: false
  });

  const triggerRef = useRef<HTMLElement>(null);
  const unwrappedTriggerRef = useFocusableRef(
    triggerRef as unknown as FocusableRef<HTMLElement>
  );

  const { triggerProps, overlayProps } = useOverlayTrigger(
    { type: 'listbox' },
    state,
    triggerRef
  );

  const { hoverProps } = useHover({ isDisabled });

  const [, setFocusWithin] = useState(false);

  const { focusProps } = useFocus({
    onBlur: () => {},
    onFocusChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
    isDisabled
  });

  const onClose = () => state.close();

  const [isFocus, setIsFocus] = useState(false);

  const onFocus = () => {
    setIsFocus(true);
  };

  const onBlur = () => {
    setIsFocus(false);
  };

  return (
    <div className={cn('flex flex-col justify-center')}>
      <Label labelClassName={cn('mb-1', 'informedInputLabelText')}>
        {label}
      </Label>
      <PressResponder {...mergeProps(hoverProps)}>
        <FocusRing
          autoFocus={autoFocus}
          within
          focusClassProps={`ring-offset-0 ${
            error
              ? 'ring-0 border-1 border-red-400'
              : isFocus
                ? `ring ring-blue-200`
                : ''
          }`}
        >
          <ComboBoxButton
            buttonStyles={getMobileComboboxWrapperStyles(
              isFocus,
              error,
              isDisabled,
              comboboxButtonClassname
            )}
            state={state}
            triggerProps={triggerProps}
            ref={triggerRef}
            hoverProps={hoverProps}
            focusProps={focusProps}
            onPress={() => state.open(null, 'manual')}
            isDisabled={isDisabled}
            onFocus={onFocus}
            onBlur={onBlur}
            className="informedInputContainerClassName"
          >
            <span
              className={cn(
                'text-sm leading-5 font-normal mr-1 mobile-selected-item-class-name',
                {
                  'text-blue-gray-900': state.selectedItem
                },
                {
                  'text-blue-gray-500': !state.selectedItem
                },
                error && !isFocus ? 'text-red-800' : '',
                'informedInputField'
              )}
              {...getDataMaskAttribute(enableDataMask)}
            >
              {state.selectedItem
                ? getDisplayValueFromSelectedState(state.selectedItem.value)
                : mobilePlaceholder
                  ? mobilePlaceholder
                  : 'Select'}
            </span>
          </ComboBoxButton>
        </FocusRing>
      </PressResponder>
      <TextFieldHint
        id={'combobox-hint'}
        errorText={isFocus ? '' : error}
        hint={helperText}
        hintClassName={hintClassName}
      />
      <Tray
        isOpen={state.isOpen}
        onClose={onClose}
        label={label}
        isSearchable={isSearchable}
        isFixedHeight
        isNonModal
        trayChildrenWrapperClassName={trayChildrenWrapperClassName}
        trayContainerClassName={trayContainerClassName}
        {...overlayProps}
      >
        <CombobboxTray
          {...props}
          state={state}
          onClose={onClose}
          overlayProps={overlayProps}
          unwrappedTriggerRef={unwrappedTriggerRef}
        />
      </Tray>
    </div>
  );
};

const CombobboxTray = (
  props: CombobboxTrayProps & SpectrumComboBoxProps<any> & ComboBoxPropsTypes
) => {
  const {
    state,
    apiStatus,
    unwrappedTriggerRef,
    overlayProps,
    onClose,
    isSearchable = true,
    loadingState,
    onLoadMore,
    cancelButtonClassname,
    renderLoader,
    handleKeyDown,
    enableDataMask,
    ...otherProps
  } = props;

  const isAsync = loadingState != null;

  const inputRef: RefObject<HTMLInputElement> = React.useRef(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const listBoxRef = useRef(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const layout = useListBoxLayout(state);
  const { inputProps, labelProps } = useComboBox(
    {
      ...otherProps,
      inputRef,
      buttonRef: unwrappedTriggerRef,
      listBoxRef,
      popoverRef,
      keyboardDelegate: layout
    },
    state
  );

  useEffect(() => {
    if (state.isOpen && inputRef.current) {
      timerRef.current = setTimeout(() => {
        //FIXME: This is a hack to fix the issue of the input not being focused when the popover is open
        inputRef.current?.focus();
      }, 500);
    }
    return () => {
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, [state.isOpen]);

  const onClickClearIcon = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    state.setInputValue('');
    inputRef.current?.focus();
  };

  const ref = React.useRef(null);
  const { menuProps } = useSelect(props as any, state, ref);

  //TODO: Abstract this function and use the same in both the desktop and mobile combobox
  const renderNoResults = () => (
    <div
      className={cn(
        'm-auto pb-3 w-full h-full flex items-center justify-center flex-col'
      )}
    >
      {/* <NoResultsFoundIcon /> FIXME: fix icon */}
      <p className={cn('text-center text-blue-gray-600 font-medium')}>
        {isAsync && loadingState === 'loading'
          ? 'Loading...'
          : 'No Results Found'}
      </p>
    </div>
  );

  const clearIcon = () =>
    inputProps.value ? (
      <div
        className={cn(' cursor-pointer focus:border-black focus:border-1 ')}
        onClick={onClickClearIcon}
      >
        {/* <CrossIcon /> FIXME: fix icon */}
      </div>
    ) : null;

  const listBox = (
    <FocusScope restoreFocus>
      <ListBoxBase
        {...menuProps}
        state={state}
        ref={listBoxRef}
        layout={layout}
        isLoading={
          loadingState === 'loadingMore' || loadingState === 'filtering'
        }
        onLoadMore={onLoadMore}
        shouldSelectOnPressUp
        renderEmptyState={renderNoResults}
        UNSAFE_className={cn('outline-none')}
        autoFocus={false}
      />
    </FocusScope>
  );

  return (
    <Container className={cn('flex h-full flex-col w-full')} ref={popoverRef}>
      {apiStatus === LOADING ? (
        <div className={cn('flex justify-center items-center p-4')}>
          <Loader fill={'#64748b'} />
        </div>
      ) : (
        <FocusScope restoreFocus contain>
          <div
            className={cn(
              'justify-between items-center bg-transparent w-full mx-16px mt-16px flex-shrink-0 position-relative',
              isSearchable ? 'flex' : 'hidden',
              'informedInputContainerClassName comboboxContainerClassName'
            )}
            style={{ paddingRight: '28px' }}
          >
            <DismissButton onDismiss={onClose} />
            <TextFieldBase
              {...props}
              inputProps={inputProps}
              labelProps={labelProps}
              ref={inputRef}
              inputContainerClassName={cn(
                'rounded-12px mt-16px textFieldBaseClassName'
              )}
              containerClassName={cn(isSearchable ? 'flex flex-1' : 'hidden')}
              // leftElement={SearchIcon} FIXME: fix icon
              defaultValue={''}
              // rightElement={clearIcon} FIXME: fix icon
              rightElement={renderLoader}
              handleKeyDown={handleKeyDown}
              enableDataMask={enableDataMask}
            />
            <button
              onClick={() => state.toggle()}
              className={
                (cn(
                  'cursor-pointer flex items-center justify-center mx-auto outline-none border-none shrink-0'
                ),
                cancelButtonClassname)
              }
            >
              <span
                className={cn('font-medium text-blue-600  absolute')}
                style={{
                  top: '20px',
                  right: '20px'
                }}
              >
                <CrossIcon />
              </span>
            </button>
            <DismissButton onDismiss={onClose} />
          </div>
          {listBox}
        </FocusScope>
      )}
    </Container>
  );
};
